import type {
  ConversationSummary,
  MessageSummary,
  SystemSenderIdentity,
} from '@tabbio/types';

export const DEFAULT_SYSTEM_SENDER_IDENTITY: SystemSenderIdentity =
  Object.freeze({
    key: 'tabbio',
    name: 'Tabbio',
    avatarUrl: null,
  });

export const SYSTEM_SENDER_AVATAR_FILENAMES = Object.freeze({
  tabbio: 'admin-sender-tabbio.png',
  founder: 'admin-sender-founder.png',
} satisfies Record<SystemSenderIdentity['key'], string>);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeAvatarUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;

  try {
    const url = new URL(value.trim());
    const isLocalDevelopmentUrl =
      url.protocol === 'http:' &&
      (url.hostname === 'localhost' || url.hostname === '127.0.0.1');
    return url.protocol === 'https:' || isLocalDevelopmentUrl
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

export function normalizeSystemSenderIdentity(
  value: unknown,
): SystemSenderIdentity | null {
  if (!isRecord(value)) return null;
  if (value.key !== 'tabbio' && value.key !== 'founder') return null;

  const name = typeof value.name === 'string' ? value.name.trim() : '';
  if (!name) return null;

  return {
    key: value.key,
    name,
    avatarUrl: normalizeAvatarUrl(value.avatarUrl),
  };
}

export function getSystemSenderIdentityFromMessage(
  message: MessageSummary | null | undefined,
): SystemSenderIdentity | null {
  if (!message) return null;

  const directIdentity = normalizeSystemSenderIdentity(message.senderIdentity);
  if (directIdentity) return directIdentity;

  for (const attachment of message.attachments) {
    if (!isRecord(attachment) || attachment.kind !== 'admin_communication') {
      continue;
    }
    const identity = normalizeSystemSenderIdentity(attachment.senderIdentity);
    if (identity) return identity;
  }

  return null;
}

/**
 * True only for a row the platform wrote: no sender and type `system`. Both
 * halves matter. Account deletion leaves sender-less `text` rows, and until
 * the API allowlisted user-sent types (security finding M1) a user could store
 * a `system` row under their own id. Neither may render as Tabbio.
 */
export function isPlatformSystemMessage(
  message: Pick<MessageSummary, 'senderId' | 'type'> | null | undefined,
): boolean {
  return !!message && message.senderId === null && message.type === 'system';
}

export function getSystemSenderIdentityFromNotificationData(
  data: unknown,
): SystemSenderIdentity | null {
  return isRecord(data)
    ? normalizeSystemSenderIdentity(data.senderIdentity)
    : null;
}

export function getSystemConversationIdentity(
  conversation: ConversationSummary,
): SystemSenderIdentity | null {
  if (conversation.type !== 'system') return null;

  return (
    getSystemSenderIdentityFromMessage(conversation.lastMessage) ??
    normalizeSystemSenderIdentity(conversation.systemSenderIdentity) ??
    DEFAULT_SYSTEM_SENDER_IDENTITY
  );
}

export function isSystemConversationReadOnly(
  conversation: Pick<ConversationSummary, 'type'>,
): boolean {
  return conversation.type === 'system';
}
